import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, FormControlLabel, Grid, MenuItem, Stack, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import HomeIcon from '@mui/icons-material/Home';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SectionHeader from '../../../components/shared/SectionHeader';
import EditableField from '../../../components/EditableField';
import BorrowerPhoneNumbersField, { BorrowerPhonesReadOnly } from './BorrowerPhoneNumbersField';
import { useConfirmModal } from '../../../components/shared/ConfirmModal';
import { MARITAL_STATUS_TYPES, CITIZENSHIP_TYPES, NAME_SUFFIXES, RESIDENCY_BASIS_TYPES, US_STATES, UNIT_TYPES } from '../../../services/mock/urlaEnumerations';

/**
 * URLA Section 1a — Borrower Information
 * Covers: personal info, contact info, current address, previous address (if < 2 years),
 * mailing address (with same-as toggle), and subject property toggle.
 * Renders for both primary borrower and co-borrower.
 */

const maskSsn = (raw) => {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return '—';
  const last4 = digits.slice(-4);
  return `***-**-${last4}`;
};

const formatSsnInput = (raw) => {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const MaskedSsnField = ({ value, onChange, isEditMode, required }) => {
  if (!isEditMode) {
    return (
      <Box sx={{ py: 0.5 }}>
        <Typography variant="caption" color="text.secondary">SSN</Typography>
        <Typography variant="body2">{maskSsn(value)}</Typography>
      </Box>
    );
  }

  return (
    <TextField
      fullWidth size="small" label="SSN" required={required}
      type="password"
      value={formatSsnInput(value)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
        onChange(formatSsnInput(digits));
      }}
      placeholder="***-**-1234"
      inputProps={{ maxLength: 11, autoComplete: 'off' }}
      helperText={value ? maskSsn(value) : undefined}
      sx={{ mb: 2 }}
    />
  );
};

import DataItem from '../../../components/shared/DataItem';

const AddressBlock = ({ title, address, namePath, isEditMode, showResidency = true, showMoveOut = false }) => {
  if (!address) return null;

  const formatAddress = (addr) => {
    const parts = [addr.street];
    if (addr.unit) parts[0] += `, ${addr.unitType || ''} ${addr.unit}`.trim();
    parts.push(`${addr.city}, ${addr.state} ${addr.zip}`);
    if (addr.country && addr.country !== 'US') parts.push(addr.country);
    return parts.join('\n');
  };

  if (!isEditMode) {
    return (
      <Box sx={{ mb: 3 }}>
        {title && <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{title}</Typography>}
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}><DataItem label="Address"><span style={{ whiteSpace: 'pre-line' }}>{formatAddress(address)}</span></DataItem></Grid>
          {showResidency && (
            <Grid size={{ xs: 6, sm: 3 }}><DataItem label="Housing">{address.occupancyType || '—'}</DataItem></Grid>
          )}
          {showResidency && address.occupancyType === 'Rent' && address.monthlyRent > 0 && (
            <Grid size={{ xs: 6, sm: 3 }}><DataItem label="Monthly Rent">${address.monthlyRent?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</DataItem></Grid>
          )}
          {address.moveInDate && <Grid size={{ xs: 6, sm: 3 }}><DataItem label="Move-In Date">{new Date(address.moveInDate).toLocaleDateString()}</DataItem></Grid>}
          {showMoveOut && address.moveOutDate && <Grid size={{ xs: 6, sm: 3 }}><DataItem label="Move-Out Date">{new Date(address.moveOutDate).toLocaleDateString()}</DataItem></Grid>}
          {address.yearsAtAddress != null && (
            <Grid size={{ xs: 6, sm: 3 }}>
              <DataItem label="Time at Address">
                {address.yearsAtAddress >= 1
                  ? `${Math.floor(address.yearsAtAddress)} yr ${Math.round((address.yearsAtAddress % 1) * 12)} mo`
                  : `${Math.round(address.yearsAtAddress * 12)} months`}
              </DataItem>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {title && <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{title}</Typography>}
      <Grid container spacing={2}>
        <Grid size={8}><EditableField label="Street Address" value={address.street} name={`${namePath}.street`} isEditMode placeholder="123 Main St" /></Grid>
        <Grid size={2}><EditableField label="Unit Type" value={address.unitType} name={`${namePath}.unitType`} isEditMode options={UNIT_TYPES} /></Grid>
        <Grid size={2}><EditableField label="Unit #" value={address.unit} name={`${namePath}.unit`} isEditMode placeholder="4B" /></Grid>
        <Grid size={5}><EditableField label="City" value={address.city} name={`${namePath}.city`} isEditMode placeholder="Los Angeles" /></Grid>
        <Grid size={3}><EditableField label="State" value={address.state} name={`${namePath}.state`} isEditMode options={US_STATES} /></Grid>
        <Grid size={2}><EditableField label="ZIP" value={address.zip} name={`${namePath}.zip`} isEditMode placeholder="90001" /></Grid>
        <Grid size={2}><EditableField label="Country" value={address.country} name={`${namePath}.country`} isEditMode options={[{ value: 'US', label: 'US' }, { value: 'Other', label: 'Other' }]} /></Grid>
      </Grid>
      {showResidency && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={4}><EditableField label="Housing" value={address.occupancyType} name={`${namePath}.occupancyType`} isEditMode options={RESIDENCY_BASIS_TYPES} /></Grid>
          {address.occupancyType === 'Rent' && (
            <Grid size={4}><EditableField label="Monthly Rent" value={address.monthlyRent} name={`${namePath}.monthlyRent`} isEditMode type="currency" placeholder="2,800" /></Grid>
          )}
          <Grid size={4}><EditableField label="Move-In Date" value={address.moveInDate} name={`${namePath}.moveInDate`} isEditMode type="date" /></Grid>
        </Grid>
      )}
      {showMoveOut && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={4}><EditableField label="Move-Out Date" value={address.moveOutDate} name={`${namePath}.moveOutDate`} isEditMode type="date" /></Grid>
        </Grid>
      )}
    </Box>
  );
};

const BorrowerSection = ({ borrower, label, namePath, isEditMode, subjectProperty }) => {
  if (!borrower) return null;

  const { control, setValue, getValues } = useFormContext();
  const [mailingMatchesCurrent, setMailingMatchesCurrent] = useState(!borrower.mailingAddress);
  const [addressIsSubject, setAddressIsSubject] = useState(borrower.currentAddressIsSubjectProperty ?? false);

  const previousAddress = borrower.previousAddresses?.[0] || null;
  const showPreviousAddress = borrower.currentAddress?.yearsAtAddress != null && borrower.currentAddress.yearsAtAddress < 2;

  const handleSubjectPropertyToggle = () => {
    const newVal = !addressIsSubject;
    setAddressIsSubject(newVal);
    if (newVal && subjectProperty) {
      const spAddr = subjectProperty.address || {};
      const currentAddr = getValues(`${namePath}.currentAddress`) || {};
      setValue(`${namePath}.currentAddressIsSubjectProperty`, true, { shouldDirty: true });
      setValue(`${namePath}.currentAddress`, { ...currentAddr, street: spAddr.street || '', unit: spAddr.unit || '', city: spAddr.city || '', state: spAddr.state || '', zip: spAddr.zip || '', country: spAddr.country || 'US' }, { shouldDirty: true });
    } else {
      setValue(`${namePath}.currentAddressIsSubjectProperty`, false, { shouldDirty: true });
    }
  };

  const handleMailingToggle = () => {
    const newVal = !mailingMatchesCurrent;
    setMailingMatchesCurrent(newVal);
    if (newVal) {
      setValue(`${namePath}.mailingAddress`, null, { shouldDirty: true });
    } else {
      setValue(`${namePath}.mailingAddress`, borrower.mailingAddress || { street: '', unit: '', city: '', state: '', zip: '', country: 'US' }, { shouldDirty: true });
    }
  };

  const sp = isEditMode ? 2 : 1;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{label}</Typography>

      {/* Personal Information */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title={<SectionHeader title="Personal Information" icon={<PersonIcon fontSize="small" />} sx={{ mb: 0 }} />} />
        <CardContent>
          <Grid container spacing={sp}>
            {isEditMode ? (
              <>
                <Grid size={{ xs: 6, sm: 3 }}><EditableField label="First Name" value={borrower.firstName} name={`${namePath}.firstName`} isEditMode required placeholder="John" /></Grid>
                <Grid size={{ xs: 6, sm: 3 }}><EditableField label="Middle Name" value={borrower.middleName} name={`${namePath}.middleName`} isEditMode placeholder="Michael" /></Grid>
                <Grid size={{ xs: 6, sm: 3 }}><EditableField label="Last Name" value={borrower.lastName} name={`${namePath}.lastName`} isEditMode required placeholder="Smith" /></Grid>
                <Grid size={{ xs: 6, sm: 3 }}><EditableField label="Suffix" value={borrower.suffix} name={`${namePath}.suffix`} isEditMode options={NAME_SUFFIXES} /></Grid>
              </>
            ) : (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}><DataItem label="Full Name"><strong>{[borrower.firstName, borrower.middleName, borrower.lastName, borrower.suffix].filter(Boolean).join(' ')}</strong></DataItem></Grid>
            )}
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <EditableField label="Date of Birth" value={borrower.dateOfBirth} name={`${namePath}.dateOfBirth`} isEditMode={isEditMode} type="date" required={isEditMode} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Controller name={`${namePath}.ssn`} control={control} render={({ field }) => (
                <MaskedSsnField value={field.value} onChange={field.onChange} isEditMode={isEditMode} required={isEditMode} />
              )} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <EditableField label="Citizenship" value={isEditMode ? borrower.citizenshipType : (CITIZENSHIP_TYPES.find(c => c.value === borrower.citizenshipType)?.label || borrower.citizenshipType)} name={`${namePath}.citizenshipType`} isEditMode={isEditMode} options={isEditMode ? CITIZENSHIP_TYPES : null} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <EditableField label="Marital Status" value={isEditMode ? borrower.maritalStatus : (MARITAL_STATUS_TYPES.find(m => m.value === borrower.maritalStatus)?.label || borrower.maritalStatus)} name={`${namePath}.maritalStatus`} isEditMode={isEditMode} options={isEditMode ? MARITAL_STATUS_TYPES : null} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              {isEditMode ? (
                <EditableField label="# of Dependents" value={borrower.dependentCount} name={`${namePath}.dependentCount`} isEditMode type="number" placeholder="0" />
              ) : (
                <DataItem label="Dependents">{borrower.dependentCount || 0}{borrower.dependentAges?.length ? ` (ages: ${borrower.dependentAges.join(', ')})` : ''}</DataItem>
              )}
            </Grid>
            {isEditMode && (
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <EditableField label="Ages of Dependents" value={(borrower.dependentAges || []).join(', ')} name={`${namePath}.dependentAges`} isEditMode placeholder="5, 8" />
              </Grid>
            )}
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Credit Score</Typography>
                {borrower.creditScore ? (
                  <Box><Chip label={borrower.creditScore} color="info" variant="outlined" /></Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">See Credit tab</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title={<SectionHeader title="Contact Information" icon={<ContactMailIcon fontSize="small" />} sx={{ mb: 0 }} />} />
        <CardContent>
          <Grid container spacing={sp}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <EditableField label="Email" value={borrower.email} name={`${namePath}.email`} isEditMode={isEditMode} type="email" required={isEditMode} placeholder="john@email.com" />
            </Grid>
            <Grid size={12}>
              {isEditMode ? (
                <BorrowerPhoneNumbersField namePath={namePath} />
              ) : (
                <BorrowerPhonesReadOnly borrower={borrower} />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Current Address */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={<SectionHeader title="Current Address" icon={<HomeIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={isEditMode && subjectProperty ? (
            <FormControlLabel control={<Switch checked={addressIsSubject} onChange={handleSubjectPropertyToggle} size="small" />} label="Same as subject property" />
          ) : undefined}
        />
        <CardContent>
          {!isEditMode && addressIsSubject && (
            <Alert severity="info" sx={{ mb: 2 }}>Current address is the subject property</Alert>
          )}
          <AddressBlock title="" address={borrower.currentAddress} namePath={`${namePath}.currentAddress`} isEditMode={isEditMode} showResidency />
        </CardContent>
      </Card>

      {/* Previous Address — only shown if < 2 years at current */}
      {showPreviousAddress && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={<SectionHeader title="Previous Address" icon={<HomeIcon fontSize="small" />} sx={{ mb: 0 }} />}
            action={<Chip label="Required — less than 2 years at current address" size="small" color="warning" variant="outlined" />}
          />
          <CardContent>
            <AddressBlock title="" address={borrower.previousAddresses?.[0]} namePath={`${namePath}.previousAddresses.0`} isEditMode={isEditMode} showResidency showMoveOut />
          </CardContent>
        </Card>
      )}

      {/* Mailing Address */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={<SectionHeader title="Mailing Address" icon={<MarkunreadMailboxIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={<FormControlLabel control={<Switch checked={mailingMatchesCurrent} onChange={handleMailingToggle} disabled={!isEditMode} size="small" />} label="Same as current address" />}
        />
        <CardContent>
          {mailingMatchesCurrent ? (
            <Typography variant="body2" color="text.secondary"><CheckIcon sx={{ fontSize: 16, mr: 0.5 }} />Mailing address is the same as the current address above.</Typography>
          ) : (
            <AddressBlock title="" address={borrower.mailingAddress} namePath={`${namePath}.mailingAddress`} isEditMode={isEditMode} showResidency={false} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const BORROWER_TYPE_OPTIONS = [
  { value: 'Spouse Co-Borrower', label: 'Spouse / Married Co-Borrower' },
  { value: 'Non-Spouse Co-Borrower', label: 'Non-Spouse Co-Borrower (parent, relative, etc.)' },
];

const EMPTY_BORROWER = {
  borrowerType: 'Non-Spouse Co-Borrower',
  firstName: '', middleName: '', lastName: '', suffix: '',
  email: '', phone: '', phoneNumbers: [],
  ssn: '', dateOfBirth: '', citizenshipType: 'USCitizen',
  maritalStatus: '', dependentCount: 0, dependentAges: [],
  creditScore: null,
  currentAddress: { street: '', unit: '', city: '', state: '', zip: '', country: 'US', occupancyType: '', monthlyRent: 0, moveInDate: '' },
  previousAddresses: [],
  mailingAddress: null,
  currentAddressIsSubjectProperty: false,
  employment: [],
  otherIncome: [],
};

const ADD_TAB = '__add__';

const BorrowerInfoTab = ({ loanDetail, isEditMode }) => {
  const { getValues, setValue } = useFormContext();
  const [activeTab, setActiveTab] = useState(0); // 0 = primary, 1+ = additional borrowers, ADD_TAB = add form
  const [addBorrowerType, setAddBorrowerType] = useState('Spouse Co-Borrower');
  const [localBorrowers, setLocalBorrowers] = useState(() => loanDetail?.borrowers || []);

  if (!loanDetail) return null;

  const borrowers = isEditMode ? localBorrowers : (loanDetail.borrowers || []);
  const allBorrowers = borrowers.map((b, i) => ({
    ...b,
    _label: i === 0
      ? 'Primary Borrower'
      : (b.borrowerType === 'Spouse Co-Borrower'
        ? `Spouse${b.firstName ? ` — ${b.firstName} ${b.lastName || ''}`.trim() : ''}`
        : `Co-Borrower${b.firstName ? ` — ${b.firstName} ${b.lastName || ''}`.trim() : ''}`),
    _isPrimary: i === 0,
    _index: i,
  }));

  const isAddTab = activeTab === ADD_TAB;
  const safeTab = isAddTab ? ADD_TAB : Math.min(activeTab, allBorrowers.length - 1);
  const currentBorrower = isAddTab ? null : allBorrowers[safeTab];

  const handleAddBorrower = () => {
    const newBorrower = { ...JSON.parse(JSON.stringify(EMPTY_BORROWER)), borrowerType: addBorrowerType };
    const currentBorrowers = isEditMode ? (getValues('borrowers') || localBorrowers) : localBorrowers;
    const newList = [...currentBorrowers, newBorrower];
    setLocalBorrowers(newList);
    if (isEditMode) setValue('borrowers', newList, { shouldDirty: true });
    setActiveTab(newList.length - 1);
  };

  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const handleRemoveBorrower = async (index) => {
    const currentBorrowers = isEditMode ? (getValues('borrowers') || localBorrowers) : localBorrowers;
    const b = currentBorrowers[index];
    const name = b?.firstName
      ? `${b.firstName} ${b.lastName || ''}`.trim()
      : `Borrower ${index + 1}`;
    const confirmed = await confirm({
      title: 'Remove Borrower',
      message: `Remove ${name} from this loan?`,
      confirmText: 'Remove',
    });
    if (!confirmed) return;
    const newList = currentBorrowers.filter((_, i) => i !== index);
    setLocalBorrowers(newList);
    if (isEditMode) setValue('borrowers', newList, { shouldDirty: true });
    setActiveTab(0);
  };

  const handleBorrowerTypeChange = (index, newType) => {
    const currentBorrowers = isEditMode ? (getValues('borrowers') || localBorrowers) : localBorrowers;
    const newList = [...currentBorrowers];
    newList[index] = { ...newList[index], borrowerType: newType };
    setLocalBorrowers(newList);
    if (isEditMode) setValue('borrowers', newList, { shouldDirty: true });
  };

  // ── Borrower Snapshot metrics ──
  const snapshotData = (() => {
    const result = { borrowerCount: borrowers.length, borrowers: [] };
    let combinedMonthlyIncome = 0;
    let combinedOtherIncome = 0;
    let combinedHousingExpense = 0;

    for (const b of borrowers) {
      const name = [b.firstName, b.lastName].filter(Boolean).join(' ') || 'Unnamed';
      const currentJob = (b.employmentHistory || []).find(e => e.isCurrent);
      const employmentIncome = (b.employmentHistory || []).filter(e => e.isCurrent).reduce((sum, e) => sum + (e.monthlyIncome || 0), 0);
      const otherIncome = (b.otherIncome || []).reduce((sum, o) => sum + (o.monthlyAmount || 0), 0);
      const totalIncome = employmentIncome + otherIncome;
      const housingExpense = b.currentAddress?.monthlyRent || 0;
      const yearsAtJob = currentJob?.startDate ? Math.round((new Date() - new Date(currentJob.startDate)) / (1000 * 60 * 60 * 24 * 365) * 10) / 10 : null;

      combinedMonthlyIncome += employmentIncome;
      combinedOtherIncome += otherIncome;
      combinedHousingExpense += housingExpense;

      result.borrowers.push({
        name,
        creditScore: b.creditScore,
        employer: currentJob?.employerName,
        position: currentJob?.position,
        yearsAtJob,
        employmentIncome,
        otherIncome,
        totalIncome,
        housingExpense,
        citizenship: CITIZENSHIP_TYPES.find(c => c.value === b.citizenshipType)?.label || b.citizenshipType,
        maritalStatus: MARITAL_STATUS_TYPES.find(m => m.value === b.maritalStatus)?.label || b.maritalStatus,
      });
    }

    result.combinedMonthlyIncome = combinedMonthlyIncome;
    result.combinedOtherIncome = combinedOtherIncome;
    result.combinedTotalIncome = combinedMonthlyIncome + combinedOtherIncome;
    result.combinedHousingExpense = combinedHousingExpense;
    return result;
  })();

  const fmtMoney = (v) => v != null && v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
  const scoreColor = (score) => {
    if (!score) return 'text.secondary';
    if (score >= 740) return 'success.main';
    if (score >= 680) return 'info.main';
    if (score >= 620) return 'warning.main';
    return 'error.main';
  };

  const showSnapshot = borrowers.length > 0 && !isEditMode;

  // Snapshot sidebar content (reusable)
  const snapshotSidebar = showSnapshot ? (
    <Box sx={{ position: 'sticky', top: 16 }}>
      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <SectionHeader title="Borrower Snapshot" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />
          <Stack spacing={1.5}>
            {snapshotData.borrowers.map((b, i) => (
              <Box key={i}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PersonIcon sx={{ fontSize: 16, color: 'action.active' }} />
                    <Typography variant="subtitle2" fontWeight={700}>{b.name}</Typography>
                    <Chip label={i === 0 ? 'Primary' : 'Co-Borrower'} size="small" color={i === 0 ? 'primary' : 'default'} variant="outlined" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                  </Stack>
                  {b.creditScore && (
                    <Typography variant="subtitle2" fontWeight={700} color={scoreColor(b.creditScore)}>{b.creditScore}</Typography>
                  )}
                </Stack>

                <Box sx={{ pl: 0.5 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">Employment</Typography>
                    <Typography variant="caption" fontWeight={600}>{b.employer || '—'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">Position</Typography>
                    <Typography variant="caption" fontWeight={600}>{b.position || '—'}{b.yearsAtJob != null ? ` · ${b.yearsAtJob} yr` : ''}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">Monthly Income</Typography>
                    <Typography variant="caption" fontWeight={600}>{fmtMoney(b.employmentIncome)}</Typography>
                  </Stack>
                  {b.otherIncome > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">Other Income</Typography>
                      <Typography variant="caption" fontWeight={600}>{fmtMoney(b.otherIncome)}</Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">Citizenship</Typography>
                    <Typography variant="caption">{b.citizenship}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">Marital Status</Typography>
                    <Typography variant="caption">{b.maritalStatus}</Typography>
                  </Stack>
                  {b.housingExpense > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Housing Expense</Typography>
                      <Typography variant="caption" fontWeight={600}>{fmtMoney(b.housingExpense)}/mo</Typography>
                    </Stack>
                  )}
                </Box>

                {i < snapshotData.borrowers.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }} />}
              </Box>
            ))}
          </Stack>

          {/* Combined Totals (multi-borrower) */}
          {snapshotData.borrowerCount > 1 && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Combined Income</Typography>
                <Typography variant="caption" fontWeight={700}>{fmtMoney(snapshotData.combinedMonthlyIncome)}/mo</Typography>
              </Stack>
              {snapshotData.combinedOtherIncome > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">Combined Other</Typography>
                  <Typography variant="caption" fontWeight={700}>{fmtMoney(snapshotData.combinedOtherIncome)}/mo</Typography>
                </Stack>
              )}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" fontWeight={700} color="primary">Total Income</Typography>
                <Typography variant="caption" fontWeight={700} color="primary">{fmtMoney(snapshotData.combinedTotalIncome)}/mo</Typography>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  ) : null;

  return (
    <Box>
      {ConfirmModalContainer}

      <Grid container spacing={2}>
        {/* Left column: Borrower detail sections */}
        <Grid size={{ xs: 12, lg: showSnapshot ? 8 : 12 }}>

          {/* Borrower Tabs */}
          <Tabs value={isAddTab ? ADD_TAB : safeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            {allBorrowers.map((b, i) => (
              <Tab key={i} value={i} icon={i === 0 ? <PersonIcon fontSize="small" /> : <GroupIcon fontSize="small" />} iconPosition="start" label={b._label} />
            ))}
            {isEditMode && (
              <Tab value={ADD_TAB} icon={<PersonAddIcon fontSize="small" />} iconPosition="start" label="Add Borrower" />
            )}
          </Tabs>

          {/* Add Borrower Panel */}
          {isAddTab && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Add a Borrower to This Loan</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Borrower Relationship</Typography>
                    <Stack spacing={1}>
                      {BORROWER_TYPE_OPTIONS.map(opt => (
                        <Card
                          key={opt.value}
                          variant="outlined"
                          onClick={() => setAddBorrowerType(opt.value)}
                          sx={{ cursor: 'pointer', borderColor: addBorrowerType === opt.value ? 'primary.main' : 'divider', bgcolor: addBorrowerType === opt.value ? 'action.selected' : 'transparent', p: 1.5 }}
                        >
                          <Typography variant="subtitle2">{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {opt.value === 'Spouse Co-Borrower' ? 'Married partner applying jointly on the loan' : 'Parent, relative, or other non-spouse applying on the loan'}
                          </Typography>
                        </Card>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>What happens next?</Typography>
                        <Typography variant="body2"><CheckIcon sx={{ fontSize: 14, color: 'success.main', mr: 0.5 }} />A new borrower profile will be created</Typography>
                        <Typography variant="body2"><CheckIcon sx={{ fontSize: 14, color: 'success.main', mr: 0.5 }} />You can fill in their personal details</Typography>
                        <Typography variant="body2"><CheckIcon sx={{ fontSize: 14, color: 'success.main', mr: 0.5 }} />Their income & employment will appear on the Income tab</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                <Button variant="contained" startIcon={<PersonAddIcon />} sx={{ mt: 2 }} onClick={handleAddBorrower}>
                  Add {addBorrowerType === 'Spouse Co-Borrower' ? 'Spouse Co-Borrower' : 'Co-Borrower'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Active Borrower Content */}
          {currentBorrower && !isAddTab && (
            <Box>
              {!currentBorrower._isPrimary && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  {isEditMode ? (
                    <TextField select size="small" value={currentBorrower.borrowerType || 'Non-Spouse Co-Borrower'} onChange={(e) => handleBorrowerTypeChange(currentBorrower._index, e.target.value)} sx={{ minWidth: 250 }}>
                      {BORROWER_TYPE_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                  ) : (
                    <Chip label={currentBorrower.borrowerType || 'Co-Borrower'} color={currentBorrower.borrowerType === 'Spouse Co-Borrower' ? 'secondary' : 'info'} variant="outlined" />
                  )}
                  {isEditMode && (
                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleRemoveBorrower(currentBorrower._index)}>Remove Borrower</Button>
                  )}
                </Stack>
              )}

              <BorrowerSection
                borrower={currentBorrower}
                label={currentBorrower._isPrimary ? 'Primary Borrower' : currentBorrower._label}
                namePath={`borrowers.${currentBorrower._index}`}
                isEditMode={isEditMode}
                subjectProperty={loanDetail.property}
              />
            </Box>
          )}
        </Grid>

        {/* Right column: Snapshot sidebar (sticky) */}
        {showSnapshot && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            {snapshotSidebar}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BorrowerInfoTab;
